import { ArrowLeft, LoaderCircle, ShieldCheck, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";
import { getPackageIdForPlanType } from "@/lib/billing";
import { getReferralDisplayState } from "@/lib/referrals";

const formatCurrency = (value) => `$${Number(value).toFixed(2)}`;

export const AmbassadorCheckoutPage = ({ resolveUserDashboardPath, user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [foundingMembership, setFoundingMembership] = useState({ total_spots: 18, active_count: 1, pending_count: 5, available_count: 12, remaining_count: 12 });
  const cancelledCheckout = searchParams.get("billing_status") === "cancelled";
  const referralState = getReferralDisplayState(searchParams.get("ref"));
  const referralCode = referralState.activeCode;
  const packageId = getPackageIdForPlanType(user?.plan_type || "Alpha Investor");
  const monthlyPrice = user?.plan_type === "Elite Team" ? 899 : user?.plan_type === "Digital Agent" ? 129 : 249;

  useSeo({
    title: "Founding 18 Checkout | SG Prop Portal",
    description: "Publish your Private Showroom and unlock the public Signature portal.",
  });

  const hasAccess = useMemo(() => {
    if (!user) return false;
    if (user.role === "agent") return false;
    return user.role === "pending_agent" && Boolean(user.claim_verified_at) && Boolean(user.is_ambassador_portal);
  }, [user]);

  useEffect(() => {
    if (!user || !hasAccess) return;
    const loadFoundingMembership = async () => {
      try {
        const response = await api.get("/dashboard/summary", authConfig());
        if (response.data?.founding_membership) {
          setFoundingMembership(response.data.founding_membership);
        }
      } catch {
      }
    };

    loadFoundingMembership();
  }, [hasAccess, user]);

  if (!user) return <Navigate replace to="/admin/login" />;
  if (user.role === "agent") return <Navigate replace to={resolveUserDashboardPath(user)} />;
  if (!hasAccess) return <Navigate replace to={resolveUserDashboardPath(user)} />;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await api.post(
        "/billing/checkout-session",
        {
          package_id: packageId,
          origin_url: window.location.origin,
          checkout_context: "ambassador_founding",
          referral_code: referralCode,
        },
        authConfig(),
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to start the founding checkout"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#121212_0%,#1A1A1A_52%,#2B2824_100%)] px-6 py-10 text-[#F6F1E9]" data-testid="ambassador-checkout-page">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-[32px] border border-[#D4AF37]/18 bg-[rgba(26,26,26,0.88)] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.24)] backdrop-blur md:p-8" data-testid="ambassador-checkout-shell">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]">Founding 18 Checkout</p>
              <h1 className="mt-3 font-[Cormorant_Garamond,serif] text-5xl font-bold leading-none" data-testid="ambassador-checkout-heading">Secure your founding spot and publish instantly.</h1>
            </div>
            <button className="outline-button !border-white/15 !text-[#F6F1E9]" data-testid="ambassador-checkout-back-button" onClick={() => navigate(resolveUserDashboardPath(user))} type="button">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Cockpit
            </button>
          </div>

          {cancelledCheckout ? (
            <div className="mt-6 rounded-[24px] border border-[#D4AF37]/22 bg-[#2A2724] px-5 py-4 text-sm leading-7 text-[#B8B0A5]" data-testid="ambassador-checkout-cancelled-banner">
              Checkout was not completed. Your verification is saved — your portal remains in Private Showroom mode until payment succeeds.
            </div>
          ) : null}

          {referralCode ? (
            <div className="mt-6 rounded-[24px] border border-[#D4AF37]/16 bg-[#1A1A1A] px-5 py-4 text-sm leading-7 text-[#F6F1E9]" data-testid="ambassador-checkout-referral-banner">
              <p data-testid="ambassador-checkout-referral-banner-primary">
                {referralState.isLockedToDifferentCode
                  ? <>This browser is already locked to referral code <span className="text-[#D4AF37]">{referralCode}</span> for 60 days.</>
                  : referralState.isStoredWithoutQuery
                    ? <>This browser currently has first-click referral attribution locked for 60 days: <span className="text-[#D4AF37]">{referralCode}</span></>
                    : <>Referral attribution is locked on first click for 60 days. Current code: <span className="text-[#D4AF37]">{referralCode}</span></>}
              </p>
              {referralState.isLockedToDifferentCode ? (
                <p className="mt-2 text-xs font-medium leading-6 text-[#C7BCA8]" data-testid="ambassador-checkout-referral-banner-secondary">
                  This link used <span className="font-semibold text-[#D4AF37]">{referralState.incomingCode}</span>, but it will not override the existing lock in this browser.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="space-y-5 rounded-[28px] border border-[#D4AF37]/16 bg-[#232323] p-6" data-testid="ambassador-checkout-summary-card">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/22 bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold text-[#F6F1E9]" data-testid="ambassador-checkout-discount-pill">
                <Sparkles className="h-4 w-4 text-[#D4AF37]" /> {foundingMembership.active_count} active • {foundingMembership.pending_count} pending • {foundingMembership.available_count} available
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.18em] text-[#D4AF37]">Your plan</p>
                <h2 className="mt-3 text-3xl font-semibold text-white">Founding 18 • {user.plan_type}</h2>
              </div>
              <div className="rounded-[24px] border border-[#D4AF37]/16 bg-[#1A1A1A] p-5" data-testid="ambassador-checkout-price-card">
                <p className="text-sm text-[#B8B0A5]">Founding 18 publishing subscription</p>
                <p className="mt-3 font-[Cormorant_Garamond,serif] text-5xl font-bold text-white">{formatCurrency(monthlyPrice)}/mo</p>
                <p className="mt-3 text-sm leading-7 text-[#B8B0A5]">This launches a real Stripe test checkout. Successful payment flips your showroom public and preserves the founding metadata in billing records.</p>
              </div>
            </div>

            <div className="space-y-5 rounded-[28px] border border-[#D4AF37]/16 bg-[#232323] p-6" data-testid="ambassador-checkout-activation-card">
              <div>
                <p className="text-sm uppercase tracking-[0.18em] text-[#D4AF37]">Pay-to-publish trigger</p>
                <h2 className="mt-3 text-3xl font-semibold text-white">After payment, your showroom goes fully live.</h2>
              </div>
              <div className="rounded-[24px] border border-[#D4AF37]/16 bg-[#1A1A1A] p-5 text-sm leading-7 text-[#B8B0A5]" data-testid="ambassador-checkout-readiness-card">
                <p className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Claim & Verify saved to the database</p>
                <p className="mt-3 inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Listings will be injected into the global marketplace instantly</p>
                <p className="mt-3 inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Public profile badge and master admin lead tracking will activate on success</p>
              </div>
              <button className="gold-button !w-full !justify-center" data-testid="ambassador-checkout-complete-button" disabled={loading} onClick={handleCheckout} type="button">
                {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Secure My Founding Spot & Go Live
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};